import { useRouter } from 'next/router';
import React, { useMemo, useState, useEffect } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { useContext } from 'use-context-selector';
import { isChLocale, storeLocale } from '@/root/constants';
import { FilterAppliedMiddleware } from 'components/algoliaHelpers/FilterAppliedMiddleware';
import { CategoryHeaderConnected } from 'components/categoryHeader/CategoryHeader';
import { CollectionFilters } from 'components/collectionFilters/CollectionFilters';
import { CustomerContext } from 'components/customerContext/CustomerContext';
import { useFeatureFlags } from 'components/featureFlagsProvider/FeatureFlagsContext';
import { GlobalContentContext } from 'components/globalContentContext/GlobalContentContext';
import { PLPContent } from 'components/plpContent/PLPContent';
import { PLPProvider } from 'components/plpContext/PLPProvider';

import { generateProTagFilter } from 'components/shelf/Shelf';
import { algoliaProductIndex } from 'utils/algoliaConstants';
import { algoliaRouting } from 'utils/algoliaRouting';

import { searchClient } from 'utils/search-client';
import type { CollectionsProps } from './collectionsProps';

export const CollectionsRoute = ({ props }: { props: CollectionsProps }) => {
  const { handle, keyHandle } = props;
  const { proCustomer } = useContext(CustomerContext);
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);
  const indexName = algoliaProductIndex(locale);
  const { featureFlags } = useFeatureFlags();
  const { isProProductFilteringEnabled } = featureFlags[locale];
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  /**
   * Set up Algolia routing config
   * - Grabs the filter settings data from Contentful and passes it to the
   *   `algoliaRouting` options.
   * - Uses `useMemo` to prevent resetting the routing object on re-render.
   */
  const { filterSettings } = useContext(GlobalContentContext);
  const productFilterSettings = filterSettings?.product;
  const routing = useMemo(
    () => algoliaRouting({ filterSettings: productFilterSettings }, locale),
    []
  );

  useEffect(() => {
    const hash = window.location.hash;

    if (!hash) {
      return;
    }

    const targetId = hash.substring(1); // Remove the '#' from the hash

    const scrollToSection = (targetElement: HTMLElement) => {
      const navbarElement = document.getElementById('header__masthead');
      const navbarHeight = navbarElement?.offsetHeight || 0;

      const targetPosition =
        targetElement.getBoundingClientRect().top +
        window.scrollY -
        navbarHeight;
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    };

    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      // If the element is already in the DOM, scroll to it immediately
      scrollToSection(targetElement);
    } else {
      // Use MutationObserver to wait for the element to be added to the DOM
      const observer = new MutationObserver(() => {
        const observedElement = document.getElementById(targetId);
        if (observedElement) {
          scrollToSection(observedElement);
          observer.disconnect(); // Stop observing once the element is found
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      // Cleanup the observer when the component unmounts
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // handle is local collection handle, keyHandle is the global collection handle
  let collectionHandle = handle ?? keyHandle;
  // If the locale is CH, always use keyHandle (handle is just for collection URL not for querying Algolia)
  if (isChLocale(locale)) {
    collectionHandle = keyHandle || handle;
  }

  const instantSearchConfigure = (
    <Configure
      filters={[
        `collections:${collectionHandle}`,
        generateProTagFilter(isProProductFilteringEnabled, proCustomer, ''),
      ].join(' AND ')}
      ruleContexts={['collection-page']}
      distinct
    />
  );

  const handleAlgoliaRefinementChange = (algoliaFiltersApplied: boolean) => {
    if (algoliaFiltersApplied !== hasFiltersApplied) {
      setHasFiltersApplied(algoliaFiltersApplied);
    }
  };

  return (
    <PLPProvider handle={handle} proCustomer={proCustomer}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        routing={routing}
      >
        {instantSearchConfigure}

        <CategoryHeaderConnected />

        {/**
         * Key is passed to `CollectionFilters` to reset the filters on
         * collection change.
         * - This works because the Algolia behavior is to reset the filter
         *   state when any filter components are re-rendered.
         * - Using `key` allows us to re-render when the collection handle is
         *   changed.
         */}
        <CollectionFilters key={handle} />

        <FilterAppliedMiddleware
          setAlgoliaRefinement={handleAlgoliaRefinementChange}
          locale={locale}
        />

        <PLPContent
          collectionHandle={handle}
          keyHandle={keyHandle}
          indexName={indexName}
          {...{ searchClient, hasFiltersApplied }}
        />
      </InstantSearch>
    </PLPProvider>
  );
};
